<template>
  <div class="xl-header-container">
    <!-- Top navigation bar with main categories -->
    <div class="top-nav w-full">
      <div class="w-full flex justify-center items-center py-4">
        <div class="max-w-screen-xl w-full mx-auto px-4">
          <div class="main-nav flex items-center justify-center gap-10">
            <a 
              v-for="(item, index) in filteredItems"
              :key="index"
              class="nav-item relative group"
              :class="{ 'has-submenu': item.items }"
              :href="item.to || '#'"
              :to="item.to"
              @click="trackEvent('XLMenu', item.label, item.type)"
            >
              <div class="flex items-center gap-2 text-[#192339] hover:text-[#3b5998] font-medium text-lg py-2 whitespace-nowrap">
                <Icon v-if="item.iconName" :icon="item.iconName" width="22" height="22" />
                <span>{{ item.label }}</span>
                <Icon 
                  v-if="item.items" 
                  icon="mdi:chevron-down" 
                  width="18" 
                  height="18" 
                />
              </div>
              
              <!-- Mega menu dropdown for items with subitems -->
              <div 
                v-if="item.items" 
                class="mega-menu absolute left-1/2 transform -translate-x-1/2 pt-6 w-auto min-w-[800px] max-w-[1200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50"
              >
                <div class="bg-white rounded-lg shadow-xl p-8 grid" 
                     :class="[item.items.length > 6 ? 'grid-cols-3' : 'grid-cols-2']">
                  <div 
                    v-for="(subitem, subIdx) in item.items" 
                    :key="`${index}-${subIdx}`"
                    class="p-4"
                  >
                    <a 
                      :href="subitem.to" 
                      :to="subitem.to"
                      class="flex items-start gap-3 hover:bg-gray-50 p-3 rounded-lg transition-colors"
                      :class="{ 'external-link': subitem.type === 'external' }"
                      @click.stop="trackEvent('XLSubMenu', subitem.label, subitem.type)"
                    >
                      <div class="icon-container bg-gray-100 p-2 rounded-full">
                        <Icon 
                          v-if="subitem.iconName" 
                          :icon="subitem.iconName" 
                          width="24" 
                          height="24" 
                          class="text-[#3b5998]"
                        />
                        <Icon 
                          v-else 
                          :icon="getDefaultIcon(item.label)" 
                          width="24" 
                          height="24" 
                          class="text-[#3b5998]"
                        />
                      </div>
                      <div class="flex flex-col overflow-hidden max-w-[200px]">
                        <span class="font-semibold text-[#192339] truncate">{{ subitem.label }}</span>
                        <span v-if="getItemDescription(subitem.label)" class="text-sm text-gray-600 mt-1 truncate">
                          {{ getItemDescription(subitem.label) }}
                        </span>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </a>
            
            <!-- Customer service special button -->
            <a 
              href="/customerservice" 
              to="/customerservice"
              class="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-full flex items-center gap-2 transition-colors shadow-md"
              @click="trackEvent('XLMenu', 'Customer Service', 'router')"
            >
              <Icon icon="line-md:heart-filled" class="animate-pulse-slow" width="20" height="20" />
              <span class="font-bold">Customer Service</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { mainMenuItems, contactInfo } from '@/data/navigation';

export default {
  name: 'AppHeaderExtraLarge',
  components: {
    Icon
  },
  data() {
    return {
      items: mainMenuItems,
      contactInfo,
      // Sample descriptions for some menu items
      productDescriptions: {
        'Home Insurance': 'Protect your home and belongings',
        'Auto Insurance': 'Coverage for your vehicles',
        'Flood Insurance': 'Protection from water damage',
        'Life Insurance': 'Financial security for your family',
        'Medicare': 'Health coverage for seniors',
        'Business Insurance': 'Protection for your company',
        'Umbrella Insurance': 'Extra liability coverage',
      }
    };
  },
  computed: {
    filteredItems() {
      // Filter out the Customer Service item from the navigation
      return this.items.filter(item => item.label !== 'Customer Service');
    }
  },
  methods: {
    trackEvent(category, label, type) {
      // Track menu clicks via analytics if needed
      if (window.gtag) {
        window.gtag('event', 'menu_click', {
          'event_category': category,
          'event_label': label,
          'link_type': type
        });
      }
    },
    getItemDescription(label) {
      return this.productDescriptions[label] || '';
    },
    getDefaultIcon(categoryLabel) {
      const iconMap = {
        'Insurance Products': 'mdi:shield',
        'Member Companies': 'mdi:office-building',
        'About Us': 'mdi:information-outline',
        'Customer Service': 'mdi:account-supervisor'
      };
      
      return iconMap[categoryLabel] || 'mdi:chevron-right';
    }
  }
};
</script>

<style scoped>
.xl-header-container {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  position: relative;
  z-index: 1000;
}

.nav-item {
  position: relative;
}

.mega-menu {
  max-height: 80vh;
  overflow-y: auto;
}

/* Animation for pulse */
@keyframes pulse-slow {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.animate-pulse-slow {
  animation: pulse-slow 2s infinite;
}

/* Bottom border animation for main nav items */
.nav-item::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background-color: #3b5998;
  transition: all 0.3s ease;
  transform: translateX(-50%);
}

.nav-item:hover::after {
  width: 100%;
}

/* External links */
.external-link {
  position: relative;
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Ensure all menu items have consistent width and truncate text appropriately */
.mega-menu a {
  width: 100%;
}
</style> 